/**
 * LogoAssemble — branded wordmark + accent bar.
 *
 * Used as the title of authentication screens (Sign Up, Sign In, Password
 * recovery), as the header logo, and as the compact corner/avatar mark.
 * The wordmark itself comes from Figma's baked-curves SVG export — paths
 * are embedded inline so the letter `fill` can flip with the surface tone
 * the logo sits on. The red accent bar is always the brand red and never
 * changes.
 *
 * Two forms, selected by `compact`:
 *   - Full (default) — 113×30 viewBox, the complete "AdSensor" wordmark.
 *   - Compact         — 38×38 viewBox, "Ad" only with a thicker accent bar.
 *
 * Two surface tones via `background`:
 *   - 'dark'  (default) → letters white  (#FFFFFF)
 *   - 'light'           → letters black  (#000000)
 *
 * Compact-only `withBackdrop` flag draws an inverse-colour pill behind the
 * mark, with letters flipping to the opposite scheme (white pill + black
 * letters on a dark canvas, black pill + white letters on a light one).
 *
 * Custom height via `size`: pass a number to override the canonical 30 /
 * 38 height. Everything (logo SVG, prefix SVG) scales proportionally so
 * the mark stays internally consistent at any resolution.
 *
 * The logo is integral — there are no flags to hide the wordmark or the
 * accent bar. Either you render LogoAssemble or you don't.
 *
 * @format
 */

import {forwardRef} from 'react';

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';

import {useSurfaceContext} from 'ui/theme/surfaceContext';

import brandTokens from 'ui/tokens/semantic/brand.json';
import spacingTokens from 'ui/tokens/semantic/spacing.json';

import {v} from 'ui/tokens/util';

import {LOGO_PREFIXES, type LogoPrefixKey, type LogoPrefixData} from './prefixes.generated';

export type {LogoPrefixKey, LogoPrefixData};
/** Re-export so stories and external consumers see the same map. */
export {LOGO_PREFIXES};

const ACCENT_FILL = v(brandTokens.brand.accent); // #FF0000
const PREFIX_GAP = parseInt(v(spacingTokens.spacing['xs-plus'])); // 6 — token, not magic

/**
 * Public enum for the surface tone. Stories import this directly so the
 * background control auto-syncs if the enum changes.
 */
export const LOGO_BACKGROUNDS = ['dark', 'light'] as const;
export type LogoBackground = (typeof LOGO_BACKGROUNDS)[number];

export interface LogoAssembleProps {
  /**
   * Surface tone the logo sits on. `dark` (default) paints the letters
   * white; `light` paints them black. Red accent bar is unaffected.
   */
  background?: LogoBackground;

  /**
   * Compact form (38×38, "Ad" only, thicker bar). Default `false` (full
   * wordmark). Use for header chrome, favicons, square avatars — anywhere
   * the full wordmark would be too wide.
   */
  compact?: boolean;

  /**
   * **Compact mode only.** Draws an inverse-colour pill behind the mark.
   * Ignored (no-op) when `compact` is `false` — the full-form logo has no
   * backdrop variant. On a dark canvas: white pill, black letters; on a
   * light canvas: black pill, white letters. Use when the compact mark
   * sits on a busy or low-contrast surface and needs a guaranteed
   * legible area around the glyphs.
   */
  withBackdrop?: boolean;

  /**
   * Override the rendered height in pixels. When set, every element (logo
   * SVG, prefix SVG, accent bar) scales proportionally so the mark stays
   * internally consistent. When `undefined` (default), uses the canonical
   * 30px (full) or 38px (compact) height.
   */
  size?: number;

  /**
   * Baked-curves prefix to render before the wordmark. Accepts either:
   *  - a registry key (typed `LogoPrefixKey`) — looked up in
   *    `LOGO_PREFIXES`, produced at build time by
   *    `scripts/generate-logo-prefixes.mjs`. **Use this in production.**
   *  - raw `LogoPrefixData` — `{ text, pathD, width, height }`. Used by
   *    the Storybook story to live-bake ad-hoc text through the Vite
   *    dev middleware. Not intended for production code.
   *
   * Roboto Flex never enters the browser bundle either way — only the
   * path data does.
   */
  prefix?: LogoPrefixKey | LogoPrefixData;

  /** Forwarded HTML className for one-off positioning. */
  className?: string;
}

// ── SVG paths ──────────────────────────────────────────────────────────────
// Full form — eight letter paths plus the accent bar, all baked from Figma.

const FULL_LETTER_PATHS: readonly string[] = [
  'M25.2422 12.043C24.8985 11.5586 24.4297 11.168 23.836 10.8711C23.2422 10.5742 22.5312 10.4258 21.7032 10.4258C20.1328 10.4258 18.8672 11.0078 17.9062 12.1719C16.9532 13.3359 16.4766 14.8242 16.4766 16.6367V17.0117C16.4766 18.8242 16.9493 20.3164 17.8946 21.4883C18.8398 22.6523 20.1133 23.2344 21.7148 23.2344C22.543 23.2344 23.2696 23.0625 23.8946 22.7188C24.5273 22.375 24.9883 21.9531 25.2773 21.4531H25.3477L25.4297 23H27.8438V5.02344H25.2422V12.043ZM25.2305 19.2031C25.0038 19.7266 24.6367 20.1875 24.1288 20.5859C23.6288 20.9766 23.0157 21.1719 22.2891 21.1719C21.2188 21.1719 20.4258 20.7812 19.9102 20C19.4023 19.2188 19.1485 18.2422 19.1485 17.0703V16.5898C19.1485 15.418 19.4062 14.4414 19.9219 13.6602C20.4375 12.8789 21.2305 12.4883 22.3008 12.4883C23.0352 12.4883 23.6485 12.6875 24.1407 13.0859C24.6407 13.4766 25.0038 13.9297 25.2305 14.4453V19.2031Z',
  'M6.48047 5.83203L0 23H2.61328L4.15808 18.7812H11.2286L12.7617 23H15.6211L9.09375 5.83203H6.48047ZM4.93909 16.6484L7.23047 10.3906C7.32422 10.1328 7.40234 9.88281 7.46484 9.64062C7.53516 9.39062 7.59766 9.14062 7.65234 8.89062H7.75781C7.8125 9.14062 7.87109 9.38672 7.93359 9.62891C8.00391 9.87109 8.08594 10.125 8.17969 10.3906L10.4536 16.6484H4.93909Z',
  'M37.8398 13.1445C36.0663 12.6523 34.9062 12.2148 34.3593 11.832C33.8124 11.4414 33.539 10.8984 33.539 10.2031C33.539 9.46094 33.8007 8.87109 34.3242 8.43359C34.8554 7.99609 35.6757 7.77734 36.7851 7.77734C37.9023 7.77734 38.7421 8.03906 39.3046 8.5625C39.8671 9.07812 40.1484 9.75781 40.1484 10.6016V10.7773H42.8437V10.5898C42.8437 9.11328 42.2929 7.91016 41.1913 6.98047C40.0976 6.05078 38.6288 5.58594 36.7851 5.58594C34.9413 5.58594 33.4882 6.03125 32.4257 6.92188C31.3632 7.8125 30.832 8.94922 30.832 10.332C30.832 11.6836 31.246 12.7344 32.0742 13.4844C32.9023 14.2344 34.2343 14.8633 36.0702 15.3711C37.8202 15.8633 38.9921 16.3281 39.5859 16.7656C40.1796 17.1953 40.4765 17.7812 40.4765 18.5234C40.4765 19.3125 40.1679 19.9492 39.5507 20.4336C38.9335 20.918 38.0507 21.1602 36.9023 21.1602C35.746 21.1602 34.8437 20.918 34.1952 20.4336C33.5546 19.9492 33.2343 19.2266 33.2343 18.2656V18.0781H30.5156V18.2773C30.5156 19.8711 31.1015 21.1172 32.2734 22.0156C33.4452 22.9062 34.9882 23.3516 36.9023 23.3516C38.8163 23.3516 40.3398 22.9023 41.4726 22.0039C42.6132 21.1055 43.1835 19.918 43.1835 18.4414C43.1757 16.9883 42.7343 15.8672 41.8593 15.0781C40.9843 14.2891 39.6445 13.6445 37.8398 13.1445Z',
  'M50.7657 10.3086C49.0469 10.3086 47.6562 10.8945 46.5938 12.0664C45.5312 13.2383 45 14.7617 45 16.6367V17.0117C45 18.8945 45.5273 20.4219 46.5821 21.5938C47.6446 22.7656 49.1485 23.3516 51.0938 23.3516C52.2969 23.3516 53.3243 23.1016 54.1758 22.6016C55.0352 22.1016 55.6602 21.5312 56.0508 20.8906L54.293 19.6133C53.9571 20.0664 53.5196 20.4609 52.9805 20.7969C52.4493 21.1328 51.8125 21.3008 51.0703 21.3008C49.9141 21.3008 49.0508 20.9023 48.4805 20.1055C47.9767 19.3848 47.7113 18.4919 47.6587 17.4453H56.1797V16.4258C56.1797 14.543 55.7071 13.0547 54.7617 11.9609C53.8163 10.8594 52.4844 10.3086 50.7657 10.3086ZM53.5782 15.5586H47.7126C47.8229 14.7815 48.0584 14.1057 48.4336 13.543C48.9648 12.7383 49.7617 12.3359 50.8243 12.3359C51.7071 12.3359 52.3867 12.625 52.8633 13.2031C53.3398 13.7734 53.5782 14.4688 53.5782 15.2891V15.5586Z',
  'M89.7422 10.3086C87.9375 10.3086 86.4844 10.8945 85.3828 12.0664C84.2891 13.2305 83.7422 14.7539 83.7422 16.6367V17.0117C83.7422 18.9023 84.2891 20.4336 85.3828 21.6055C86.4844 22.7695 87.9375 23.3516 89.7422 23.3516C91.5469 23.3516 92.9961 22.7695 94.0898 21.6055C95.1836 20.4336 95.7305 18.9023 95.7305 17.0117V16.6367C95.7305 14.7539 95.1836 13.2305 94.0898 12.0664C92.9961 10.8945 91.5469 10.3086 89.7422 10.3086ZM93.0586 17.082C93.0586 18.3164 92.7773 19.3242 92.2148 20.1055C91.6523 20.8867 90.8282 21.2773 89.7422 21.2773C88.6485 21.2773 87.8203 20.8867 87.2578 20.1055C86.6953 19.3242 86.4141 18.3164 86.4141 17.082V16.5781C86.4141 15.3438 86.6953 14.3359 87.2578 13.5547C87.8282 12.7656 88.6562 12.3711 89.7422 12.3711C90.8203 12.3711 91.6407 12.7656 92.2032 13.5547C92.7735 14.3359 93.0586 15.3438 93.0586 16.5781V17.082Z',
  'M104.8 10.3672C104.605 10.3281 104.417 10.3086 104.238 10.3086C103.48 10.3086 102.824 10.5078 102.269 10.9062C101.714 11.2969 101.266 11.7539 100.921 12.2773H100.875L100.781 10.6602H98.3555V23H100.968V14.6797C101.227 14.1172 101.625 13.6523 102.164 13.2852C102.71 12.9102 103.359 12.7227 104.109 12.7227C104.328 12.7227 104.527 12.7382 104.707 12.7578C104.886 12.7772 105.058 12.7969 105.222 12.8281V10.4727C105.136 10.4414 104.996 10.4062 104.8 10.3672Z',
  'M77.7071 15.6523C76.3633 15.3477 75.4805 15.0703 75.0586 14.8203C74.6367 14.5625 74.4258 14.2188 74.4258 13.7891C74.4258 13.3594 74.6055 13.0039 74.9648 12.7227C75.3243 12.4414 75.8946 12.3008 76.6758 12.3008C77.4648 12.3008 78.0469 12.457 78.4219 12.7695C78.7969 13.082 78.9844 13.5117 78.9844 14.0586V14.1992H81.5743V14.1055C81.5743 12.9414 81.125 12.0195 80.2266 11.3398C79.336 10.6523 78.1562 10.3086 76.6875 10.3086C75.211 10.3086 74.0352 10.6484 73.1602 11.3281C72.2852 12 71.8477 12.8711 71.8477 13.9414C71.8477 14.9492 72.2188 15.7461 72.961 16.332C73.7032 16.9102 74.7578 17.3555 76.125 17.668C77.461 17.9648 78.3203 18.2461 78.7032 18.5117C79.0938 18.7773 79.2891 19.1641 79.2891 19.6719C79.2891 20.1719 79.0663 20.582 78.6211 20.9023C78.1758 21.2148 77.5547 21.3711 76.7578 21.3711C75.961 21.3711 75.3203 21.207 74.836 20.8789C74.3594 20.5508 74.1211 20.0508 74.1211 19.3789V19.2793H71.5312V19.3555C71.5312 20.5898 71.9805 21.5664 72.8788 22.2852C73.7773 22.9961 75.0703 23.3516 76.7578 23.3516C78.4532 23.3516 79.7305 23 80.5898 22.2969C81.4493 21.5859 81.8788 20.6562 81.8788 19.5078C81.8788 18.4219 81.5235 17.5859 80.8125 17C80.1094 16.4062 79.0743 15.957 77.7071 15.6523Z',
  'M64.957 10.4141C64.1835 10.4141 63.4765 10.5742 62.8359 10.8945C62.1952 11.2148 61.6718 11.6406 61.2656 12.1719H61.207L61.1132 10.6602H58.6992V23H61.3124V14.4219C61.5859 13.8516 61.9726 13.3945 62.4726 13.0508C62.9726 12.707 63.5351 12.5352 64.1601 12.5352C64.9101 12.5352 65.4843 12.7305 65.8827 13.1211C66.289 13.5039 66.4921 14.1523 66.4921 15.0664V23H69.1054V15.1133C69.1054 13.4102 68.7265 12.2031 67.9687 11.4922C67.2109 10.7734 66.207 10.4141 64.957 10.4141Z',
];

const FULL_ACCENT_BAR_D = 'M112.105 0H109.105V30H112.105V0Z';

// Compact form — "Ad" two-glyph mark with a thicker bar. From Figma's
// "Компактный логотип" export.

const COMPACT_LETTER_PATHS: readonly string[] = [
  // A
  'M9.375 9.83203L2.89453 27H5.50781L7.05261 22.7812H14.1232L15.6562 27H18.5156L11.9883 9.83203H9.375ZM13.3482 20.6484L11.0742 14.3906C10.9805 14.125 10.8984 13.8711 10.8281 13.6289C10.7656 13.3867 10.707 13.1406 10.6523 12.8906H10.5469C10.4922 13.1406 10.4297 13.3906 10.3594 13.6406C10.2969 13.8828 10.2188 14.1328 10.125 14.3906L7.83358 20.6484H13.3482Z',
  // d
  'M20.8008 16.1719C19.8477 17.3359 19.3711 18.8242 19.3711 20.6367V21.0117C19.3711 22.8242 19.8438 24.3164 20.7891 25.4883C21.7344 26.6523 23.0078 27.2344 24.6094 27.2344C25.4375 27.2344 26.1641 27.0625 26.7891 26.7188C27.4219 26.375 27.8828 25.9531 28.1719 25.4531H28.2422L28.3242 27H30.7383V9.02344H28.1367V16.043C27.793 15.5586 27.3242 15.168 26.7305 14.8711C26.1367 14.5742 25.4258 14.4258 24.5977 14.4258C23.0273 14.4258 21.7617 15.0078 20.8008 16.1719ZM22.8047 24C22.2969 23.2188 22.043 22.2422 22.043 21.0703V20.5898C22.043 19.418 22.3008 18.4414 22.8164 17.6602C23.332 16.8789 24.125 16.4883 25.1953 16.4883C25.9297 16.4883 26.543 16.6875 27.0352 17.0859C27.5352 17.4766 27.8984 17.9297 28.125 18.4453V23.2031C27.8984 23.7266 27.5312 24.1875 27.0234 24.5859C26.5234 24.9766 25.9102 25.1719 25.1836 25.1719C24.1133 25.1719 23.3203 24.7812 22.8047 24Z',
];

const COMPACT_ACCENT_BAR_D = 'M38 0H34V38H38V0Z';

interface LogoForm {
  /** Natural viewBox width — used by both `viewBox` and aspect-ratio math. */
  vbWidth: number;
  /** Natural viewBox height — also the natural rendered height when `size` is undefined. */
  vbHeight: number;
  letterPaths: readonly string[];
  accentBarD: string;
  /**
   * SVG fill-rule for the letter paths. Compact uses `evenodd` so the
   * interior holes (the triangle inside `A`, the bowl of `d`) render as
   * cut-outs rather than filled. The full-form export was authored to
   * work with the default `nonzero` rule and doesn't need this.
   */
  fillRule: 'nonzero' | 'evenodd';
}

const FORM_FULL: LogoForm = {
  vbWidth: 113,
  vbHeight: 30,
  letterPaths: FULL_LETTER_PATHS,
  accentBarD: FULL_ACCENT_BAR_D,
  fillRule: 'nonzero',
};

const FORM_COMPACT: LogoForm = {
  vbWidth: 38,
  vbHeight: 38,
  letterPaths: COMPACT_LETTER_PATHS,
  accentBarD: COMPACT_ACCENT_BAR_D,
  fillRule: 'evenodd',
};

/** Prefix SVGs are baked at this height — see prefixes.config.json `lineHeight`. */
const PREFIX_NATURAL_HEIGHT = 30;

// ── Styled wrappers ────────────────────────────────────────────────────────

const Root = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: PREFIX_GAP,
});

// ── Component ──────────────────────────────────────────────────────────────

export const LogoAssemble = forwardRef<HTMLDivElement, LogoAssembleProps>(function LogoAssemble(
  {background: backgroundProp, compact = false, withBackdrop = false, size, prefix, className},
  ref,
) {
  const form = compact ? FORM_COMPACT : FORM_FULL;

  // Surface-context-aware default. When `background` is not provided,
  // derive it from the surrounding SurfaceContextProvider — the canvas
  // tells us what tone the letters should be. Explicit prop wins.
  //   dark context  → background='dark'  → letters white
  //   light context → background='light' → letters black
  const inheritedContext = useSurfaceContext();
  const background: LogoBackground = backgroundProp ?? inheritedContext;

  // Colour resolution.
  //
  // `letterStandard` is the natural letter colour on the active canvas —
  // white on dark, black on light. Used for the prefix (which always sits
  // on the bare canvas, never on the backdrop) and for the logo's own
  // letters when there's no backdrop.
  //
  // `letterInverted` is the opposite. When the compact backdrop is on,
  // the backdrop pill takes `letterStandard` (so it stands out against
  // the canvas) and the LOGO's letters take `letterInverted` (so they
  // stand out against the pill). The prefix, sitting outside the pill,
  // keeps `letterStandard`.
  //
  // The accent bar is always #FF0000 — brand identity, not styled.
  const letterStandard = background === 'dark' ? '#FFFFFF' : '#000000';
  const letterInverted = background === 'dark' ? '#000000' : '#FFFFFF';
  const backdropActive = compact && withBackdrop;
  const logoLetterFill = backdropActive ? letterInverted : letterStandard;
  const backdropFill = letterStandard;
  const prefixFill = letterStandard;

  // Logo sizing — render height defaults to the form's natural height
  // (30 / 38). Width scales proportionally so aspect ratio stays exact.
  const renderHeight = size ?? form.vbHeight;
  const logoWidth = form.vbWidth * (renderHeight / form.vbHeight);

  // Prefix lookup — typeof discriminates the LogoPrefixKey | LogoPrefixData union.
  const prefixEntry: LogoPrefixData | undefined =
    typeof prefix === 'string' ? LOGO_PREFIXES[prefix] : prefix;

  // Prefix sizing — independent of the logo form. Its baked natural
  // height (30) is the reference. Compact toggle does NOT change the
  // prefix's text size; only an explicit `size` override does.
  const prefixDisplayH = size ?? PREFIX_NATURAL_HEIGHT;
  const prefixScale = prefixDisplayH / PREFIX_NATURAL_HEIGHT;
  const prefixWidth = prefixEntry ? prefixEntry.width * prefixScale : 0;

  const accessibleName = [prefixEntry?.text, 'AdSensor'].filter(Boolean).join(' ').trim();

  return (
    <Root ref={ref} className={className} aria-label={accessibleName}>
      {prefixEntry && (
        <svg
          width={prefixWidth}
          height={prefixDisplayH}
          viewBox={`0 0 ${prefixEntry.width} ${prefixEntry.height}`}
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
          aria-hidden='true'
        >
          <path d={prefixEntry.pathD} fill={prefixFill} />
        </svg>
      )}

      <svg
        width={logoWidth}
        height={renderHeight}
        viewBox={`0 0 ${form.vbWidth} ${form.vbHeight}`}
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
        aria-hidden='true'
      >
        {backdropActive && <rect width={form.vbWidth} height={form.vbHeight} fill={backdropFill} />}
        <path d={form.accentBarD} fill={ACCENT_FILL} />
        {form.letterPaths.map((d, i) => (
          <path key={i} d={d} fill={logoLetterFill} fillRule={form.fillRule} />
        ))}
      </svg>
    </Root>
  );
});
